import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, UserPlus, AlertCircle, CheckCircle2, Building2, 
  Briefcase, Mail, Phone, Calendar, User, DollarSign, MapPin, Hash,
  Shield, UserCheck
} from 'lucide-react';

import { useEmployeeStore } from '../../../store/employeeStore';
import { useAttendanceStore } from '../../../store/attendanceStore';
import { apiClient } from '../../../lib/api';
import { createEmployeeSchema } from '../../../utils/validation';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { PageHeader } from '../../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Field, FormSection } from '../../../components/Field';
import { Separator } from '../../../components/Separator';
import { DepartmentField } from './DepartmentField';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/Select';

interface RoleOption {
  id: string;
  code: string;
  name: string;
  description?: string;
}

const DEFAULT_ROLES: RoleOption[] = [
  { id: '7bf8530e-e956-4cdb-9664-025b13c4bc23', code: 'employee', name: 'Employee', description: 'Standard portal access (Attendance, Leave, Shifts, Payroll)' },
  { id: 'eeac6bda-231a-409d-b707-3fe611a07746', code: 'reporting_manager', name: 'Reporting Manager', description: 'Team management, approvals, performance reviews' },
  { id: '895f5044-f63b-4f8e-b298-8a928aefc7d9', code: 'hr_admin', name: 'HR Admin', description: 'People hub, recruitment, onboarding, workforce' },
  { id: '51f08807-84e3-409e-9234-0fc801245f61', code: 'super_admin', name: 'Super Admin', description: 'Full system access & platform administration' },
  { id: '39113f11-1cf3-41b0-8991-c8be082e4ff9', code: 'attendance_admin', name: 'Attendance Admin', description: 'Attendance, shifts, and biometric log management' },
  { id: '169c3253-bdaa-4a60-beb5-83eea4d08982', code: 'payroll_admin', name: 'Payroll Admin', description: 'Payroll processing and compensation management' },
  { id: '0da3dc47-d194-4db6-8c4b-7ca77476cb1a', code: 'recruitment_admin', name: 'Recruitment Admin', description: 'Job openings, candidates, and hiring pipelines' },
  { id: '0ea9d130-774d-42aa-b9af-7f7f0660b2bf', code: 'asset_admin', name: 'Asset Admin', description: 'IT hardware, software licenses, and inventory' },
];

const EMPLOYMENT_TYPES = [
  { value: 'full-time', label: 'Regular / Full-time Employee', description: 'Permanent full-time team member' },
  { value: 'contract', label: 'Contract Employee', description: 'Fixed-term contract engagement' },
  { value: 'freelance', label: 'Freelancer', description: 'Independent external specialist' },
  { value: 'part-time', label: 'Part-time Employee', description: 'Part-time working schedule' },
  { value: 'intern', label: 'Intern', description: 'Internship or training program' },
  { value: 'consultant', label: 'Consultant', description: 'Advisory or external consultancy' },
];

const CURRENCIES = [
  { code: 'USD', label: 'USD ($) - US Dollar' },
  { code: 'EUR', label: 'EUR (€) - Euro' },
  { code: 'GBP', label: 'GBP (£) - British Pound' },
  { code: 'INR', label: 'INR (₹) - Indian Rupee' },
  { code: 'CAD', label: 'CAD ($) - Canadian Dollar' },
  { code: 'AUD', label: 'AUD ($) - Australian Dollar' },
  { code: 'SGD', label: 'SGD ($) - Singapore Dollar' },
  { code: 'AED', label: 'AED (AED) - UAE Dirham' },
];

type FormValues = z.infer<typeof createEmployeeSchema>;

export function CreateEmployee() {
  const navigate = useNavigate();
  const { createEmployee, employees, fetchEmployees } = useEmployeeStore();
  const { offices, fetchOffices } = useAttendanceStore();

  const [roles, setRoles] = useState<RoleOption[]>(DEFAULT_ROLES);
  const [serverError, setServerError] = useState<string | null>(null);
  const [addAnother, setAddAnother] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchOffices();
    apiClient.get('/people/rbac/roles')
      .then((res) => {
        const rows = res.data?.data || res.data;
        if (Array.isArray(rows) && rows.length > 0) {
          setRoles(rows);
        }
      })
      .catch(() => {
        // use DEFAULT_ROLES fallback
      });
  }, [fetchEmployees, fetchOffices]);

  const defaultValues: FormValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    employeeCode: '',
    department: '',
    designation: '',
    roleId: DEFAULT_ROLES[0].id,
    managerId: '',
    joinDate: new Date().toISOString().split('T')[0],
    status: 'active',
    location: '',
    employmentType: 'full-time',
    salary: '',
    currency: 'USD',
  };

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(createEmployeeSchema),
    defaultValues,
  });

  const selectedDepartment = watch('department');

  const onSubmit = async (values: FormValues) => {
    setServerError(null);

    try {
      // Sanitize payload
      const payload: any = {
        firstName: values.firstName.trim(),
        lastName: values.lastName ? values.lastName.trim() : '',
        email: values.email.trim().toLowerCase(),
        joinDate: values.joinDate,
        status: values.status || 'active',
        department: values.department.trim(),
        designation: values.designation.trim(),
      };

      if (values.roleId && values.roleId.trim()) {
        payload.roleId = values.roleId.trim();
        const matched = roles.find(r => r.id === values.roleId || r.code === values.roleId);
        if (matched) {
          payload.role = matched.code || matched.name;
        }
      }

      if (values.phone && values.phone.trim()) {
        payload.phone = values.phone.trim();
      }
      if (values.employeeCode && values.employeeCode.trim()) {
        payload.employeeCode = values.employeeCode.trim();
      }
      if (values.managerId && values.managerId !== 'none' && values.managerId.trim()) {
        payload.managerId = values.managerId.trim();
      }
      if (values.location && values.location.trim()) {
        payload.location = values.location.trim();
      }
      if (values.employmentType && values.employmentType.trim()) {
        payload.employmentType = values.employmentType.trim();
      }
      if (values.currency && values.currency.trim()) {
        payload.currency = values.currency.trim();
      }
      if (values.salary !== undefined && values.salary !== null && String(values.salary).trim() !== '') {
        const num = Number(values.salary);
        if (!isNaN(num)) {
          payload.salary = num;
        }
      }

      await createEmployee(payload);
      toast.success(`${payload.firstName} ${payload.lastName || ''}`.trim() + ' added to team successfully');

      if (addAnother) {
        reset(defaultValues);
      } else {
        navigate('/people/employees');
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create employee. Please check the entered information.';
      setServerError(msg);
      toast.error(msg);
    }
  };

  // Managers list excluding current placeholder or inactive
  const potentialManagers = employees.filter((e) => e.status === 'active');

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <Breadcrumbs
        className="mb-4"
        items={[
          { label: 'People', href: '/people' },
          { label: 'Employees', href: '/people/employees' },
          { label: 'Add employee' },
        ]}
      />

      <PageHeader
        title="Add new employee"
        description="Quickly onboard a new team member and provision their company identity."
        actions={
          <Button variant="outline" onClick={() => navigate('/people/employees')}>
            <ArrowLeft className="size-4" /> Back to directory
          </Button>
        }
      />

      {serverError && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 size-5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Unable to create employee</p>
            <p className="mt-0.5">{serverError}</p>
          </div>
          <button
            type="button"
            onClick={() => setServerError(null)}
            className="text-xs underline underline-offset-2 hover:opacity-80"
          >
            Dismiss
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="shadow-xs border-border">
          <CardContent className="space-y-8 pt-6">
            
            {/* Section 1: Basic Identity */}
            <FormSection
              title="Personal & Identity"
              description="Name, company email, contact details, and optional employee ID."
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field
                  label="First name"
                  htmlFor="firstName"
                  required
                  error={errors.firstName?.message}
                >
                  <Input
                    id="firstName"
                    placeholder="e.g. Sarah"
                    invalid={!!errors.firstName}
                    {...register('firstName')}
                  />
                </Field>

                <Field
                  label="Last name"
                  htmlFor="lastName"
                  error={errors.lastName?.message}
                >
                  <Input
                    id="lastName"
                    placeholder="e.g. Jenkins"
                    invalid={!!errors.lastName}
                    {...register('lastName')}
                  />
                </Field>

                <Field
                  label="Work email address"
                  htmlFor="email"
                  required
                  error={errors.email?.message}
                  hint="Used for authentication, notifications, and company directory"
                >
                  <Input
                    id="email"
                    type="email"
                    leftIcon={<Mail className="size-4 text-muted-foreground" />}
                    placeholder="sarah.jenkins@company.com"
                    invalid={!!errors.email}
                    {...register('email')}
                  />
                </Field>

                <Field
                  label="Phone number"
                  htmlFor="phone"
                  error={errors.phone?.message}
                  hint="Include country code (e.g. +1 555 019 2834)"
                >
                  <Input
                    id="phone"
                    type="tel"
                    leftIcon={<Phone className="size-4 text-muted-foreground" />}
                    placeholder="+1 (555) 019-2834"
                    invalid={!!errors.phone}
                    {...register('phone')}
                  />
                </Field>

                <Field
                  label="Employee ID / Code"
                  htmlFor="employeeCode"
                  error={errors.employeeCode?.message}
                  hint="Leave blank to automatically generate an ID"
                  className="sm:col-span-2"
                >
                  <Input
                    id="employeeCode"
                    leftIcon={<Hash className="size-4 text-muted-foreground" />}
                    placeholder="e.g. EMP-1042 (Auto-generated if empty)"
                    invalid={!!errors.employeeCode}
                    {...register('employeeCode')}
                  />
                </Field>
              </div>
            </FormSection>

            <Separator />

            {/* Section 2: Organization & Employment */}
            <FormSection
              title="Role & Organization"
              description="Department, title, reporting hierarchy, and employment status."
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                
                {/* Department — from people.departments */}
                <Field
                  label="Department"
                  htmlFor="department"
                  required
                  error={errors.department?.message}
                >
                  <DepartmentField
                    value={selectedDepartment || ''}
                    onChange={(name) => setValue('department', name, { shouldValidate: true, shouldDirty: true })}
                    invalid={!!errors.department}
                  />
                </Field>


                {/* Designation / Job Title */}
                <Field
                  label="Designation / Job Title"
                  htmlFor="designation"
                  required
                  error={errors.designation?.message}
                >
                  <Input
                    id="designation"
                    leftIcon={<Briefcase className="size-4 text-muted-foreground" />}
                    placeholder="e.g. Senior Product Designer"
                    invalid={!!errors.designation}
                    {...register('designation')}
                  />
                </Field>

                {/* System Role / Access Level */}
                <Field
                  label="System Role / Access Level"
                  required
                  error={errors.roleId?.message}
                  hint="Determines portal permissions and operational capabilities in TeamOne"
                >
                  <Controller
                    control={control}
                    name="roleId"
                    render={({ field }) => {
                      const selectedRole = roles.find(r => r.id === field.value || r.code === field.value);
                      return (
                        <Select
                          value={field.value || roles.find(r => r.code === 'employee')?.id || roles[0]?.id || ''}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger invalid={!!errors.roleId}>
                            <SelectValue placeholder="Select system role">
                              {selectedRole ? (
                                <div className="flex items-center gap-2">
                                  <Shield className="size-3.5 text-primary shrink-0" />
                                  <span className="font-medium text-foreground">{selectedRole.name}</span>
                                </div>
                              ) : undefined}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {roles.map((r) => (
                              <SelectItem key={r.id} value={r.id}>
                                <div className="flex flex-col py-0.5 text-left">
                                  <div className="flex items-center gap-2">
                                    <Shield className="size-3.5 text-primary shrink-0" />
                                    <span className="font-medium text-foreground">{r.name}</span>
                                  </div>
                                  {r.description && (
                                    <span className="text-xs text-muted-foreground mt-0.5 ml-6">{r.description}</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />
                </Field>

                {/* Employment Type */}
                <Field
                  label="Employment Type"
                  required
                  error={errors.employmentType?.message}
                  hint="Staff classification and contract arrangement"
                >
                  <Controller
                    control={control}
                    name="employmentType"
                    render={({ field }) => {
                      const selectedType = EMPLOYMENT_TYPES.find(t => t.value === field.value);
                      return (
                        <Select
                          value={field.value || 'full-time'}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger invalid={!!errors.employmentType}>
                            <SelectValue placeholder="Select type">
                              {selectedType ? (
                                <div className="flex items-center gap-2">
                                  <UserCheck className="size-3.5 text-muted-foreground shrink-0" />
                                  <span className="font-medium text-foreground">{selectedType.label}</span>
                                </div>
                              ) : undefined}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {EMPLOYMENT_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                <div className="flex flex-col py-0.5 text-left">
                                  <div className="flex items-center gap-2">
                                    <UserCheck className="size-3.5 text-muted-foreground shrink-0" />
                                    <span className="font-medium text-foreground">{type.label}</span>
                                  </div>
                                  {type.description && (
                                    <span className="text-xs text-muted-foreground mt-0.5 ml-6">{type.description}</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />
                </Field>

                {/* Reporting Manager */}
                <Field
                  label="Reporting Manager"
                  error={errors.managerId?.message}
                  hint="Select direct supervisor or leader"
                >
                  <Controller
                    control={control}
                    name="managerId"
                    render={({ field }) => (
                      <Select
                        value={field.value || 'none'}
                        onValueChange={(val) => field.onChange(val === 'none' ? '' : val)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select direct manager" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            None (Executive / Top Level)
                          </SelectItem>
                          {potentialManagers.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.firstName} {m.lastName} {m.designation ? `(${m.designation})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>

                {/* Join Date */}
                <Field
                  label="Join date"
                  htmlFor="joinDate"
                  required
                  error={errors.joinDate?.message}
                >
                  <Input
                    id="joinDate"
                    type="date"
                    leftIcon={<Calendar className="size-4 text-muted-foreground" />}
                    invalid={!!errors.joinDate}
                    {...register('joinDate')}
                  />
                </Field>

                {/* Status */}
                <Field
                  label="Status"
                  required
                  error={errors.status?.message}
                >
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <Select
                        value={field.value || 'active'}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger invalid={!!errors.status}>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="on-leave">On leave</SelectItem>
                          <SelectItem value="resigned">Resigned</SelectItem>
                          <SelectItem value="terminated">Terminated</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>

              </div>
            </FormSection>

            <Separator />

            {/* Section 3: Workplace & Compensation */}
            <FormSection
              title="Workplace & Compensation"
              description="Base office location and compensation parameters (optional)."
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                
                {/* Location */}
                <Field
                  label="Work Location / Office"
                  htmlFor="location"
                  error={errors.location?.message}
                  hint="Primary physical office or remote designation"
                  className="sm:col-span-2"
                >
                  <Input
                    id="location"
                    leftIcon={<MapPin className="size-4 text-muted-foreground" />}
                    placeholder="e.g. Bangalore Head Office / Remote"
                    {...register('location')}
                  />
                </Field>

                {/* Currency */}
                <Field
                  label="Currency"
                  error={errors.currency?.message}
                >
                  <Controller
                    control={control}
                    name="currency"
                    render={({ field }) => (
                      <Select
                        value={field.value || 'USD'}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((curr) => (
                            <SelectItem key={curr.code} value={curr.code}>
                              {curr.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>

                {/* Base Salary */}
                <Field
                  label="Annual Base Salary"
                  htmlFor="salary"
                  error={errors.salary?.message}
                  hint="Optional compensation record"
                >
                  <Input
                    id="salary"
                    type="number"
                    step="any"
                    min="0"
                    leftIcon={<DollarSign className="size-4 text-muted-foreground" />}
                    placeholder="e.g. 85000"
                    {...register('salary')}
                  />
                </Field>

              </div>
            </FormSection>

          </CardContent>

          <Separator />

          <CardFooter className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/people/employees')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <div className="flex items-center gap-3">
              <Button
                type="submit"
                variant="outline"
                disabled={isSubmitting}
                onClick={() => setAddAnother(true)}
              >
                Save & add another
              </Button>
              <Button
                type="submit"
                isLoading={isSubmitting && !addAnother}
                disabled={isSubmitting}
                onClick={() => setAddAnother(false)}
              >
                <UserPlus className="size-4" /> Create employee
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
